import axios, { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios'
import { authHeaders } from '@classes/auth_service'
import { toastError, toastSuccess } from '@constants/toasts'
import { get, isArray, isEmpty, trimStart } from 'lodash'
// @ts-expect-error no declaration
import { stringify } from 'querystring-es3'
import { useEffect, useState } from 'react'

import serverUrl from '../server'

export type ApiRequestOptions = {
  successMessage?: string
  errorMessage?: string
  rethrowError?: boolean
  suppressErrorToast?: boolean
  timeout?: number
}

type HttpPayload = Record<string, unknown>

export function getServerUrl (path: string, query?: HttpPayload) {
  const base = serverUrl + '/' + path.replace(/^\//, '')
  return query && !isEmpty(query) ? base + '?' + stringify(query) : base
}

export function getScaledImageUrl (path: string, query?: HttpPayload) {
  if (Math.random() > -1) {
    return getServerUrl(path, query)
  }

  const q: HttpPayload = { ...query, p: trimStart(path, '/') }
  if (window && window.innerWidth && window.innerHeight) {
    q.w = window.innerWidth
    q.h = window.innerHeight
  }
  return getServerUrl('/api/photo', q)
}

export default function useApi () {
  const [source, setSource] = useState<CancelTokenSource | null>(null)

  function preRequest (url: string, options?: ApiRequestOptions) {
    const config: AxiosRequestConfig = {
      ...(source ? { cancelToken: source.token } : {}),
      ...(options?.timeout === undefined ? {} : { timeout: options.timeout })
    }
    const headers = authHeaders(url)

    if (Object.keys(headers).length > 0) {
      config.headers = headers
    }

    const path = url.startsWith('http') ? url : getServerUrl(url)
    return { path, config }
  }

  useEffect(() => {
    setSource(axios.CancelToken.source)

    return () => {
      if (source) {
        source.cancel('axios request cancelled')
      }
    }
  }, [])

  const handleResponse = <T>(response: AxiosResponse, resolve: (value: T) => void, options?: ApiRequestOptions) => {
    if (options && options.successMessage) {
      toastSuccess(options.successMessage)
    }
    resolve(response.data)
  }

  const handleError = (err: Error, reject: (reason?: Error) => void, options?: ApiRequestOptions) => {
    err.message = get(err, 'response.data.message', `${err}`) || 'Something went wrong, try again'
    if (options && options.rethrowError) {
      reject(err)
    }
    if (!options?.suppressErrorToast) {
      toastError(err.message)
    }
  }

  const apiGet = <T = unknown>(url: string, options?: ApiRequestOptions) =>
    new Promise<T>((resolve, reject) => {
      const { path, config } = preRequest(url, options)
      axios
        .get(path, config)
        .then(resp => handleResponse(resp, resolve, options))
        .catch(err => handleError(err, reject, options))
    })

  const apiGetArray = <T = unknown>(url: string, options?: ApiRequestOptions) =>
    apiGet<T[]>(url, options)
      .then(res => isArray(res) ? res : [])

  const apiPost = <T = unknown>(
    url: string,
    body: unknown,
    options?: ApiRequestOptions,
    onUploadProgress?: (percent: number) => void
  ) =>
    new Promise<T>((resolve, reject) => {
      const { path, config } = preRequest(url, options)
      axios.post(path, body, {
        ...config,
        ...(onUploadProgress ? {
          onUploadProgress: (event: AxiosProgressEvent) => {
            if (event.total) {
              onUploadProgress(Math.round((event.loaded / event.total) * 100))
            }
          }
        } : {})
      }).then(resp => handleResponse(resp, resolve, options)).catch(err => handleError(err, reject, options))
    })

  const apiPut = <T = unknown>(url: string, body: unknown, options?: ApiRequestOptions) =>
    new Promise<T>((resolve, reject) => {
      const { path, config } = preRequest(url, options)
      axios
        .put(path, body, config)
        .then(resp => handleResponse(resp, resolve, options))
        .catch(err => handleError(err, reject, options))
    })

  const apiPatch = <T = unknown>(url: string, body: unknown, options?: ApiRequestOptions) =>
    new Promise<T>((resolve, reject) => {
      const { path, config } = preRequest(url, options)
      axios
        .patch(path, body, config)
        .then(resp => handleResponse(resp, resolve, options))
        .catch(err => handleError(err, reject, options))
    })

  const apiDelete = <T = unknown>(url: string, body: unknown, options?: ApiRequestOptions) =>
    new Promise<T>((resolve, reject) => {
      const { path, config } = preRequest(url, options)
      axios
        .delete(path, { ...config, data: body })
        .then(resp => handleResponse(resp, resolve, options))
        .catch(err => handleError(err, reject, options))
    })

  return { apiGet, apiGetArray, apiPost, apiPut, apiPatch, apiDelete }
}
